import { useT } from "@magic-md/i18n/react";
import { Filter, Trash2, X } from "lucide-react";
import { useState } from "react";
import { type LogLevel, type LogSource, useLogStore } from "../../store/log-store";
import { getThemeAccentStyle } from "../../theme/runtime/theme-accents";
import { levelConfig } from "./log-config";

export function LogFilterBar() {
    const t = useT("editor");
    const filter = useLogStore((s) => s.filter);
    const toggleLevel = useLogStore((s) => s.toggleLevel);
    const toggleSource = useLogStore((s) => s.toggleSource);
    const setFilter = useLogStore((s) => s.setFilter);
    const resetFilter = useLogStore((s) => s.resetFilter);
    const clear = useLogStore((s) => s.clear);
    const [showFilters, setShowFilters] = useState(false);

    return (
        <div className="border-b border-border bg-muted/20">
            {/* Main bar */}
            <div className="flex items-center gap-2 px-3 py-1">
                <span className="text-[11px] font-semibold text-muted-foreground shrink-0">
                    {t("log.title")}
                </span>

                {/* Search */}
                <div className="flex-1 max-w-[240px]">
                    <input
                        type="text"
                        className="w-full rounded border border-border bg-background px-2 py-0.5 text-[11px]"
                        placeholder={t("log.searchPlaceholder")}
                        value={filter.search}
                        onChange={(e) => setFilter({ search: e.target.value })}
                    />
                </div>

                {/* Level toggles */}
                <div className="flex gap-0.5">
                    {(["debug", "info", "warn", "error"] as LogLevel[]).map((level) => {
                        const cfg = levelConfig[level];
                        const active = filter.levels.has(level);
                        return (
                            <button
                                key={level}
                                type="button"
                                className={`px-1.5 py-0.5 text-[9px] font-medium rounded transition-colors ${active
                                        ? "theme-accent-chip"
                                        : "text-muted-foreground/40 hover:text-muted-foreground"
                                    }`}
                                style={active ? getThemeAccentStyle(cfg.accent) : undefined}
                                onClick={() => toggleLevel(level)}
                                title={t("log.toggleLevel", { level: cfg.label })}
                            >
                                {cfg.label}
                            </button>
                        );
                    })}
                </div>

                {/* Advanced filter toggle */}
                <button
                    type="button"
                    className={`p-1 rounded transition-colors ${showFilters
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground/50 hover:text-muted-foreground"
                        }`}
                    onClick={() => setShowFilters(!showFilters)}
                    title={t("log.advancedFilters")}
                >
                    <Filter size={12} />
                </button>

                {/* Clear */}
                <button
                    type="button"
                    className="p-1 rounded text-muted-foreground/50 theme-accent-icon-button transition-colors"
                    style={getThemeAccentStyle("error")}
                    onClick={clear}
                    title={t("log.clearAll")}
                >
                    <Trash2 size={12} />
                </button>
            </div>

            {/* Advanced filters */}
            {showFilters && (
                <div className="flex items-center gap-3 px-3 py-1.5 border-t border-border/50 bg-muted/10">
                    <span className="text-[10px] text-muted-foreground/50">{t("log.field.source")}:</span>
                    <div className="flex gap-0.5">
                        {(
                            [
                                "system",
                                "plugin",
                                "extension",
                                "parser",
                                "resolver",
                                "ui",
                                "sidecar",
                                "engine",
                                "mcp",
                            ] as LogSource[]
                        ).map((source) => {
                            const active = filter.sources.has(source);
                            return (
                                <button
                                    key={source}
                                    type="button"
                                    className={`px-1.5 py-0.5 text-[9px] font-medium rounded transition-colors ${active
                                        ? "bg-muted text-foreground/70"
                                        : "text-muted-foreground/40 hover:text-muted-foreground"
                                        }`}
                                    onClick={() => toggleSource(source)}
                                >
                                    {t(`log.source.${source}`)}
                                </button>
                            );
                        })}
                    </div>

                    <span className="text-[10px] text-muted-foreground/50">{t("log.field.definition")}:</span>
                    <input
                        type="text"
                        className="w-24 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono"
                        placeholder={t("common.all")}
                        value={filter.unitId ?? ""}
                        onChange={(e) => setFilter({ unitId: e.target.value || undefined })}
                    />

                    <button
                        type="button"
                        className="ml-auto text-[10px] text-muted-foreground/50 hover:text-foreground flex items-center gap-0.5"
                        onClick={resetFilter}
                    >
                        <X size={10} /> {t("common.reset")}
                    </button>
                </div>
            )}
        </div>
    );
}
